'use client'

import * as React from 'react'
import { create, createStore, useStore } from 'zustand'
import { getRepoSpaceName, type Block } from '@basehub/data/replicache/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { run } from '@basehub/utils'
import { z } from 'zod'
import { isUserRepo } from '@basehub/data/replicache/mutators/_public-types'

export type RepoStoreFocusType =
  | 'block:document-title'
  | 'block:sidebar'
  | 'block:name'
  | 'block:value'
  | 'block:wrapper'
  | 'block:properties-panel'

export type RepoStore = {
  repoId?: string
  teamId?: string
  spaceId?: string
  cutBlocks: null | { ids: Block['id'][] }
  focus: {
    type: RepoStoreFocusType
    blockId: string
    temporaryBackLink?: {
      title: string
      id: string
      edgeId?: string
    }
    preventScroll?: boolean
    elementIdOverride?: string
  } | null
  editorSelectedBlockId: string | null
  setSpaceId: ({ teamId, repoId }: { teamId: string; repoId: string }) => void
  setFocus: (newFocus: RepoStore['focus']) => void
  setCutBlocks: (cutBlocks: RepoStore['cutBlocks']) => void
}

export type AIStore = {
  assistantId: string | null
  setAssistantId: (assistantId: string) => void
}

const createRepoStore = (initProps?: Partial<RepoStore>) => {
  return createStore<RepoStore>()((set) => ({
    repoId: undefined,
    teamId: undefined,
    spaceId:
      initProps?.teamId && initProps.repoId
        ? getRepoSpaceName(initProps.teamId, initProps.repoId)
        : undefined,
    cutBlocks: null,
    focus: null,
    editorSelectedBlockId: null,
    setSpaceId: ({ teamId, repoId }) =>
      set({
        teamId,
        repoId,
        spaceId: getRepoSpaceName(teamId, repoId),
      }),
    setFocus: (newFocus) => set({ focus: newFocus }),
    setCutBlocks: (cutBlocks) => set({ cutBlocks }),
    ...initProps,
  }))
}

const createAIStore = (initProps?: Partial<AIStore>) => {
  return createStore<AIStore>()((set) => ({
    assistantId: null,
    setAssistantId: (assistantId) => set({ assistantId }),
    ...initProps,
  }))
}

export const RepoStoreContext = React.createContext<ReturnType<
  typeof createRepoStore
> | null>(null)
export const AIStoreContext = React.createContext<ReturnType<
  typeof createAIStore
> | null>(null)

export function useRepoStoreRoot() {
  const store = React.useContext(RepoStoreContext)
  if (!store) throw new Error('Missing RepoStoreContext.Provider in the tree')
  return store
}

export function useRepoStore<T>(selector: (state: RepoStore) => T): T {
  const store = useRepoStoreRoot()
  return useStore(store, selector)
}

export function useAIStore<T>(selector: (state: AIStore) => T): T {
  const store = React.useContext(AIStoreContext)
  if (!store) throw new Error('Missing AIStoreContext.Provider in the tree')
  return useStore(store, selector)
}

export const RepoStoreProvider = ({
  children,
  initialValues,
}: {
  children: React.ReactNode
  initialValues?: Partial<Omit<RepoStore, 'spaceId'>> & {
    repoId: string
    teamId: string
  }
}) => {
  const store = React.useMemo(() => {
    return createRepoStore(initialValues)
  }, [initialValues])

  const activeSpaceId = useStore(store, (state) => state.spaceId)

  React.useEffect(() => {
    useActiveSpaceId.setState(activeSpaceId)
  }, [activeSpaceId])

  return (
    <RepoStoreContext.Provider value={store}>
      {children}
    </RepoStoreContext.Provider>
  )
}

export const RepoStoreReplicacheProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { data } = useRepoReplicache()
  const repoStore = React.useMemo(() => createRepoStore(), [])
  const aiStore = React.useMemo(() => createAIStore(), [])

  const setSpaceId = useStore(repoStore, (state) => state.setSpaceId)
  const setAssistantId = useStore(aiStore, (state) => state.setAssistantId)
  const assistantId = useStore(aiStore, (state) => state.assistantId)

  const teamId = data?.team.id
  const repoId = data?.repoId

  React.useEffect(() => {
    if (!teamId || !repoId) return
    setSpaceId({ teamId, repoId })
    useActiveSpaceId.setState(getRepoSpaceName(teamId, repoId))
  }, [teamId, repoId, setSpaceId])

  React.useEffect(() => {
    if (!teamId || !repoId) return
    if (assistantId) return

    if (data?.repo && isUserRepo(data.repo) && data.repo?.assistantId) {
      setAssistantId(data.repo.assistantId)
      return
    }

    run(async () => {
      const response = await fetch('/api/ai/content-assistant', {
        method: 'POST',
        body: JSON.stringify({ teamId, repoId }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.status !== 200) {
        throw new Error('Failed to create assistant')
      }

      const data = await response.json()
      return z.object({ assistantId: z.string() }).parse(data).assistantId
    }).then((assistantId) => {
      setAssistantId(assistantId)
    })
  }, [assistantId, setAssistantId, repoId, teamId, data?.repo])

  return (
    <RepoStoreContext.Provider value={repoStore}>
      <AIStoreContext.Provider value={aiStore}>
        {children}
      </AIStoreContext.Provider>
    </RepoStoreContext.Provider>
  )
}

export const useActiveSpaceId = create<undefined | string>(() => {
  return undefined
})
